import { X } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';

import { AuthError } from '../api';
import { authErrorMessage, MIN_PASSWORD_LENGTH, MIN_USERNAME_LENGTH } from '../auth';

type SignInDialogProps = {
  onSignIn(username: string, password: string): Promise<void>;
  onSignUp(input: { username: string; email: string; password: string }): Promise<void>;
  onClose(): void;
};

type Mode = 'signin' | 'signup';

/**
 * Sign-in is a dialog rather than a gate: the library is public, and an account only
 * unlocks uploading and managing your own sets.
 *
 * A pure component — it receives the two operations rather than reaching for the auth
 * client itself, which is what keeps it testable with a plain fake.
 */
export function SignInDialog({ onSignIn, onSignUp, onClose }: SignInDialogProps) {
  const [mode, setMode] = useState<Mode>('signin');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // Checked here as well as on the server so the rule is visible before a round trip,
    // rather than discovered as an error afterwards.
    if (mode === 'signup' && password.length < MIN_PASSWORD_LENGTH) {
      setError(`Passwords must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    try {
      if (mode === 'signin') {
        await onSignIn(username, password);
      } else {
        await onSignUp({ username, email, password });
        // Sign-up deliberately returns no session — approval comes first.
        setPending(true);
      }
    } catch (reason) {
      const code = reason instanceof AuthError ? reason.code : undefined;
      const fallback = reason instanceof Error ? reason.message : 'Something went wrong.';
      setError(authErrorMessage(code, fallback));
    } finally {
      setBusy(false);
    }
  }

  if (pending) {
    return (
      <Shell title="Almost there" onClose={onClose}>
        <p style={{ color: 'var(--dim)', fontSize: 'var(--step-0)' }}>
          Account created. It needs to be approved before you can sign in — you will be able
          to log in once that happens.
        </p>
        <footer>
          <button type="button" className="button primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </Shell>
    );
  }

  return (
    <Shell title={mode === 'signin' ? 'Sign in' : 'Create account'} onClose={onClose}>
      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'signin'}
          onClick={() => {
            setMode('signin');
            setError(null);
          }}
        >
          Sign in
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'signup'}
          onClick={() => {
            setMode('signup');
            setError(null);
          }}
        >
          Create account
        </button>
      </div>

      <form onSubmit={submit}>
        <p style={{ color: 'var(--dim)', fontSize: 'var(--step-0)' }}>
          {mode === 'signin'
            ? 'Listening is open to everyone. Sign in to upload and manage your sets.'
            : 'New accounts are approved by hand before they can sign in.'}
        </p>

        <label className="field">
          <span className="label">Username</span>
          <input
            type="text"
            autoComplete="username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
            minLength={MIN_USERNAME_LENGTH}
            autoFocus
          />
        </label>

        {mode === 'signup' && (
          <label className="field">
            <span className="label">Email</span>
            <input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          </label>
        )}

        <label className="field">
          <span className="label">Password</span>
          <input
            type="password"
            autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            {...(mode === 'signup' ? { 'aria-describedby': 'password-hint' } : {})}
          />
        </label>
        {/* Outside the label deliberately: nested, it would become part of the field's
            accessible name ("PasswordAt least 12 characters") instead of its description. */}
        {mode === 'signup' && (
          <span className="hint" id="password-hint">
            At least {MIN_PASSWORD_LENGTH} characters.
          </span>
        )}

        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        <footer>
          <button type="button" className="button" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? 'Working' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </footer>
      </form>
    </Shell>
  );
}

type ShellProps = {
  title: string;
  onClose(): void;
  children: ReactNode;
};

function Shell({ title, onClose, children }: ShellProps) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal login" role="dialog" aria-modal="true" aria-labelledby="signin-title">
        <header>
          <h2 id="signin-title">{title}</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={17} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
